// src/components/wellfinder/reviewMapEncoding.js
//
// THE SINGLE SOURCE FOR EVERY VISUAL ENCODING ON THE REVIEW MAP.
//
// The map's paint expressions and the on-map legend are both DERIVED from this
// object. That is the point: a legend that is written by hand alongside the
// paint is a legend that silently stops being true the first time someone tunes
// a colour, and a wrong legend is worse than none — it tells the analyst the
// orange dot means something it does not.
//
// The colours are literals here rather than Tailwind class names because
// MapLibre paint properties take CSS colours, not classes. They are kept in
// step with tailwind.config.cjs by hand, which is the one place this file can
// drift; the values are named after their tokens so the correspondence is
// visible. accent.teal #16f2d4 · accent.blue #1f9dff · accent.orange #FF7F00 ·
// slate-400 / fg.secondary #94a3b8 · slate-500 #64748b · bg.DEFAULT #050711.

export const ENCODING = Object.freeze({
  // Topmost and never occluded. A different SHAPE as well as a different
  // colour: at the p50 nearest distance of 4.78m a candidate and the detection
  // are ~19px apart at z18, and two flat circles differing only in hue read as
  // one blob exactly where the distinction matters most. White fill inside a
  // heavy blue ring survives the overlap.
  detection: {
    key: "detection",
    label: "Detection",
    fill: "#ffffff",
    stroke: "#1f9dff",       // accent.blue
    radius: 6,
    strokeWidth: 3,
  },

  // Rank 1 is the adopt default in 3c, so it is the one candidate that gets a
  // brand colour rather than a neutral.
  rank1: {
    key: "rank1",
    label: "Rank-1 candidate",
    fill: "#16f2d4",         // accent.teal
    stroke: "#050711",       // bg.DEFAULT — a hairline against bright imagery
    radius: 6,
    strokeWidth: 1.5,
  },

  // Deliberately neutral and smaller. Rank is conveyed by size and saturation,
  // not by a number on the map: twelve numbered dots inside 30m is unreadable,
  // and the panel already carries the ranks.
  candidate: {
    key: "candidate",
    label: "Other candidate",
    fill: "#64748b",         // slate-500
    stroke: "#050711",
    radius: 4,
    strokeWidth: 1.5,
  },

  // Hover and selection share one appearance. They are different states in the
  // store — hover is transient, selection is sticky and becomes 3c's adopt
  // target — but on the map they mean the same thing to the eye: "this is the
  // one you are pointing at". Two highlight colours would be a distinction the
  // analyst has to learn for no gain.
  active: {
    key: "active",
    label: "Hovered / selected",
    fill: "#FF7F00",         // accent.orange
    stroke: "#050711",
    radius: 8,
    strokeWidth: 1.5,
  },

  // BOTH failure modes were real. The wrapper bug meant it drew nothing at all;
  // once that was fixed it was still invisible, because a 1px #94a3b8 line at
  // 0.7 opacity is within a shade of the Permian's own tan-and-grey ground.
  //
  // Sized against the corpus rather than a guess: well_head_boundary areas are
  // p50 23 m2, p90 60 m2, max 1017 m2 — so a typical footprint is ~4.8m across,
  // about 19px at z18, and the smallest are 0.12 m2 (~1.4px). At that size the
  // outline IS the feature and it has to survive being small.
  //
  // Fuchsia because nothing in the Permian is fuchsia. Teal, blue and orange
  // are taken by the markers; slate, tan and olive are the ground.
  footprint: {
    key: "footprint",
    // NOT "pad". well_head_boundary is the outline of the wellhead itself —
    // p50 23 m2, and what you see in the imagery is a pumpjack. `well_pad` is a
    // separate asset_type with 95,541 rows, so the name is genuinely taken, and
    // the confusion is worst next to PadRekko where pads ARE the subject.
    label: "Wellhead footprint",
    stroke: "#e879f9",       // fuchsia-400
    width: 2,
    opacity: 1,
  },

  // The line from the detection to each candidate. Dashed so it never reads as
  // a real feature — there is no line in the world between a detection and a
  // registry point.
  link: {
    key: "link",
    label: "Detection → candidate",
    // `stroke` is the resting colour and is what the legend draws. The other
    // two are the highlighted variants and reuse rank1 / active, which the
    // legend already explains — the drift test asserts every colour this can
    // produce is covered by some legend line, and moving the footprint to
    // fuchsia is what exposed that this one was not.
    stroke: "#94a3b8",       // slate-400
    strokePrimary: "#16f2d4",
    strokeOther: "#94a3b8",
    strokeActive: "#FF7F00",
    width: 1,
    opacity: 0.9,
    dash: [2, 1],
  },

  // ── THE HOLDER: ANOTHER RECORD'S DETECTION, SHOWN ON REQUEST ─────────────
  //
  // Drawn only while the analyst has asked to see who holds a taken candidate,
  // which is why both entries carry `onlyWith` — the legend lists them only
  // while they can be on the map.
  //
  // The SAME ringed shape as our detection, because it is one: a white fill
  // says "detection", the ring colour says whose. Yellow because nothing else
  // on this map is yellow — teal, blue, orange, fuchsia and slate are taken —
  // and it holds against the tan ground where the slate link did not.
  holder: {
    key: "holder",
    label: "Holding detection",
    onlyWith: "holder",
    fill: "#ffffff",
    stroke: "#fde047",       // yellow-300
    radius: 6,
    strokeWidth: 3,
  },

  // From the holding detection to the well it holds. Dashed for the same
  // reason as the candidate links: no such line exists on the ground.
  holderLink: {
    key: "holderLink",
    label: "Holding detection → well",
    onlyWith: "holder",
    stroke: "#fde047",
    width: 1.5,
    opacity: 1,
    dash: [2, 1],
  },
});

// What the legend renders, in draw order top to bottom. Derived, so adding a
// marker type to ENCODING without adding it here is the only way the two can
// disagree — and the test asserts exactly that.
export const LEGEND_ITEMS = Object.freeze([
  ENCODING.detection,
  ENCODING.rank1,
  ENCODING.candidate,
  ENCODING.active,
  ENCODING.link,
  ENCODING.footprint,
  ENCODING.holder,
  ENCODING.holderLink,
]);

const { detection, rank1, candidate, active, footprint, link, holder, holderLink } = ENCODING;

// The holder's ring is its identity, so it is the colour the legend draws; the
// fill is shared with our detection and already explained there.
export const holderCirclePaint = () => ({
  "circle-radius": holder.radius,
  "circle-color": holder.fill,
  "circle-stroke-width": holder.strokeWidth,
  "circle-stroke-color": holder.stroke,
});

export const holderLinkPaint = () => ({
  "line-color": holderLink.stroke,
  "line-width": holderLink.width,
  "line-opacity": holderLink.opacity,
  "line-dasharray": holderLink.dash,
});

export const detectionCirclePaint = () => ({
  "circle-radius": detection.radius,
  "circle-color": detection.fill,
  "circle-stroke-width": detection.strokeWidth,
  "circle-stroke-color": detection.stroke,
});

export const candidateCirclePaint = () => ({
  "circle-radius": ["case",
    ["==", ["get", "active"], 1], active.radius,
    ["==", ["get", "primary"], 1], rank1.radius,
    candidate.radius],
  "circle-color": ["case",
    ["==", ["get", "active"], 1], active.fill,
    ["==", ["get", "primary"], 1], rank1.fill,
    candidate.fill],
  "circle-stroke-width": candidate.strokeWidth,
  "circle-stroke-color": candidate.stroke,
});

export const linkLinePaint = () => ({
  "line-color": ["case",
    ["==", ["get", "active"], 1], link.strokeActive,
    ["==", ["get", "primary"], 1], link.strokePrimary,
    link.strokeOther],
  "line-width": ["case", ["==", ["get", "active"], 1], 2, 1],
  "line-opacity": ["case", ["==", ["get", "primary"], 1], 0.9, 0.45],
  "line-dasharray": link.dash,
});

export const footprintLinePaint = () => ({
  "line-color": footprint.stroke,
  "line-width": footprint.width,
  "line-opacity": footprint.opacity,
});

// ── Scale, stated once ───────────────────────────────────────────────────────
//
// MapLibre's Transform uses _tileSize = 512 and worldSize = tileSize * 2^zoom,
// so metres per pixel is
//
//   40075016.686 * cos(lat) / (512 * 2^zoom)
//
// which at z18 and 31.7°N is 0.2540 m/px — HALF the figure the 256-tile
// convention gives. It matters for sizing the pane: covering the detection plus
// a 100m radius in every direction needs ~790px, not ~400px.
export const REVIEW_ZOOM = 18;

// ── DETAIL ZOOM ──────────────────────────────────────────────────────────────
// z18 is confirmed legible for "is there a wellhead". It is NOT reliably
// legible for "pump jack or frac well" — pass 1 zoomed on roughly a quarter of
// records, and it was the only thing that needed the mouse. At ~25% of 9,348
// A/A' records a mouse trip plus a re-decide is hours.
//
// z20, not higher, and the reason is the imagery rather than the screen:
//
//   z18  0.254 m/px   254 m across   a 9 m pump jack =  35 px   0.4x native
//   z19  0.127 m/px   127 m across                     71 px   0.8x native
//   z20  0.064 m/px    64 m across                    142 px   1.6x native
//   z21  0.032 m/px    32 m across                    283 px   3.1x native
//
// BASEMAP_10CM is 10 cm/px, so past about z19.3 there is NO NEW INFORMATION —
// z20 and z21 are upscaling. z20 is chosen anyway because the need is not more
// detail, it is a bigger picture of the detail that exists: discriminating a
// pump jack's beam from a frac tree's compact stack is a shape judgement, and
// 142 px of shape is easier for an eye than 35. z21 triples the upscaling for
// nothing and loses the surroundings that give the shape context.
//
// The DEFAULT is deliberately not raised. At z19 the viewport drops to about
// +/-73 m at a 1150 px pane and stops containing the full 100 m candidate
// radius — trading a confirmed property for an unconfirmed one.
export const DETAIL_ZOOM = 20;

// ── WHICH ZOOM A RECORD OPENS AT ─────────────────────────────────────────────
// Pass 1 measured 4.1s median on tier A — and the analyst pressed `z` on EVERY
// record, because z20 is where the equipment judgement is actually made. z18
// was not the working view; it was a view being passed through 9,348 times.
//
// So the default flips to z20 — but only where it is safe, and "safe" is
// tighter than it first looks. At z20 the binding constraint is VERTICAL,
// because the map row is clamped to 480-560px while its width follows the
// window:
//
//   pane            horizontal    vertical
//   480 x 480       +/- 15.2 m    +/- 15.2 m     <- the floor
//   765 x 520       +/- 24.3 m    +/- 16.5 m     <- 1280px window
//   1150 x 560      +/- 36.5 m    +/- 17.8 m     <- 1920px window
//
// Vertical barely moves with the window, so the honest threshold is ~16m, not
// the ~32m a width-only reading gives. A candidate further than that is off
// screen in some direction on some screen.
//
// THE TEST IS RANK-1, NOT THE FARTHEST CANDIDATE. rank-1 is the nearest
// candidate and the match tiers A and A′ arrive with, so it is what `p` approves
// on those records (since 5d5aa439 `p` adopts the selected candidate or the
// record's own match, not rank-1 as such); approving a match whose marker is
// off screen is the thing to avoid. Distant
// also-rans going out of frame is fine — a′ is DEFINED by one near candidate
// and distant others, and the panel lists them all regardless.
//
// Coverage: 62% of tier a, 91% of a', 76% of b open at z20. The rest open at
// z18 exactly as before.
export const DETAIL_ZOOM_SAFE_RADIUS_M = 16;

/**
 * The zoom a record opens at, before any manual toggle.
 * @param {object|null} detail the record detail payload
 */
export function defaultZoomFor(detail) {
  const rank1 = detail?.candidates?.find((c) => c.rank === 1);
  // No candidate at all — tier c. Context view: there is no equipment
  // judgement to make against a registry match, and the surroundings are the
  // finding.
  if (!rank1 || rank1.distance_m == null) return REVIEW_ZOOM;
  return rank1.distance_m <= DETAIL_ZOOM_SAFE_RADIUS_M ? DETAIL_ZOOM : REVIEW_ZOOM;
}
export const M_PER_PX_AT_Z18_31_7N = 0.254;
